import React, { useEffect, useRef } from "react";
import WaveSurfer from "wavesurfer.js";

interface WaveformProps {
  audioUrl: string;
  onTimeUpdate?: (currentTime: number) => void; // Callback for tracking time updates
  seek?: number; // Time to seek to and play from
  onPlay?: (instance: WaveSurfer) => void; // Callback to notify parent when playing
}

const Waveform: React.FC<WaveformProps> = ({ audioUrl, onTimeUpdate, seek, onPlay }) => {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const isReadyRef = useRef(false); // Track whether WaveSurfer is ready

  // Initialize WaveSurfer
  useEffect(() => {
    if (waveformRef.current) {
      waveSurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: "#ddd",
        progressColor: "#ff5500",
        cursorColor: "#ff5500",
        barWidth: 2,
        barRadius: 3,
        height: 200,
      });
  
      waveSurferRef.current.load(audioUrl);
  
      waveSurferRef.current.on("ready", () => {
        isReadyRef.current = true; // Mark as ready when the audio is loaded
      });
  
      if (onTimeUpdate) {
        waveSurferRef.current.on("audioprocess", () => {
          if (waveSurferRef.current) {
            onTimeUpdate(waveSurferRef.current.getCurrentTime());
          }
        });
      }
      return () => {
        try {
          // Ensure proper cleanup
          if (waveSurferRef.current) {
            waveSurferRef.current.destroy();
            waveSurferRef.current = null; // Reset the reference
          }
        } catch (error) {
          console.error("Error during WaveSurfer cleanup:", error);
        }
        isReadyRef.current = false; // Reset ready state
      };
    }
  }, [audioUrl, onTimeUpdate]);
  

  // Handle seeking
  useEffect(() => {
    if (waveSurferRef.current && isReadyRef.current && typeof seek === "number" && audioUrl) {
      const duration = waveSurferRef.current.getDuration();
      if (duration && !isNaN(duration)) {
        waveSurferRef.current.seekTo(seek / duration);
        waveSurferRef.current.play();
      }
    }
  }, [seek, audioUrl]);

  // Handle play/pause on waveform click
  const handleWaveformClick = () => {
    if (waveSurferRef.current) {
      if (waveSurferRef.current.isPlaying()) {
        waveSurferRef.current.pause();
      } else {
        waveSurferRef.current.play();
        if (onPlay) {
          onPlay(waveSurferRef.current); // Notify parent when playing
        }
      }
    }
  };

  // Handle spacebar toggle for play/pause
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Space") {
        event.preventDefault(); // Prevent default scrolling behavior
        if (waveSurferRef.current) {
          if (waveSurferRef.current.isPlaying()) {
            waveSurferRef.current.pause();
          } else {
            waveSurferRef.current.play();
            if (onPlay) {
              onPlay(waveSurferRef.current); // Notify parent when playing
            }
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onPlay]);

  return (
    <div onClick={handleWaveformClick} style={{ cursor: "pointer" }}>
      <div ref={waveformRef} />
    </div>
  );
};

export default Waveform;
